<template>
  <div class="map-panel">
    <!-- Segment: övningar / pass -->
    <div class="seg">
      <button :class="{ on: view === 'exercises' }" @click="view = 'exercises'">Övningar <span class="seg-n">{{ ds.mappings.length }}</span></button>
      <button :class="{ on: view === 'sessions' }" @click="view = 'sessions'">Pass <span class="seg-n">{{ fmtInt(ds.items.length) }}</span></button>
    </div>

    <!-- ── Övningar ── -->
    <template v-if="view === 'exercises'">
      <div class="toolbar">
        <input v-model="search" type="search" class="search" placeholder="Sök övning i filen…" />
        <div class="filters">
          <button v-for="f in filters" :key="f.key" class="fchip" :class="{ on: filter === f.key, warn: f.key === 'todo' && f.count }" @click="filter = f.key">
            {{ f.label }} <span>{{ f.count }}</span>
          </button>
        </div>
      </div>

      <div class="bulk">
        <button class="bulk-btn" @click="autoMatchUnresolved" :disabled="!todoCount">✨ Auto-matcha olösta</button>
        <button class="bulk-btn" @click="setUnresolved('create')" :disabled="!todoCount">＋ Skapa olösta som nya</button>
        <button class="bulk-btn" @click="setUnresolved('ignore')" :disabled="!todoCount">Ignorera olösta</button>
        <span class="bulk-hint">🔗 = sparad mappning · % = namnlikhet (gul = osäker, kontrollera)</span>
      </div>

      <div class="rows">
        <div v-for="m in visibleMappings" :key="m.csvName" class="mrow" :class="rowClass(m)">
          <div class="mrow-top">
            <div class="mname" :title="m.csvName">
              {{ m.csvName }}
              <span v-if="m.fromAlias" class="tag link" title="Sparad mappning">🔗</span>
              <span v-else-if="m.action === 'map' && m.exerciseTypeId === m.suggestedId && m.autoScore" class="tag score" :class="{ low: m.autoScore < 75 }" :title="m.autoScore < 75 ? 'Osäker matchning – kontrollera' : 'Namnlikhet'">{{ m.autoScore }} %{{ m.autoScore < 75 ? ' ?' : '' }}</span>
            </div>
            <div class="mmeta">{{ fmtInt(m.setCount) }} set · {{ fmtInt(m.sessionCount) }} pass</div>
          </div>

          <div class="mrow-act">
            <div class="actions">
              <button :class="{ on: m.action === 'map' }" @click="m.action = 'map'">Koppla</button>
              <button :class="{ on: m.action === 'create' }" @click="m.action = 'create'">Ny</button>
              <button :class="{ on: m.action === 'ignore' }" @click="m.action = 'ignore'">Ignorera</button>
            </div>

            <select v-if="m.action === 'map'" v-model="m.exerciseTypeId" class="et-select" :class="{ missing: !m.exerciseTypeId }">
              <option :value="null" disabled>Välj befintlig övning…</option>
              <option v-if="m.suggestedId && m.autoScore >= 40" :value="m.suggestedId">★ {{ nameOf(m.suggestedId) }} (förslag {{ m.autoScore }} %)</option>
              <option v-for="et in sortedTypes" :key="et.id" :value="et.id">{{ et.name }}</option>
            </select>

            <span v-else-if="m.action === 'ignore'" class="ignored-note">Hoppas över ({{ fmtInt(m.setCount) }} set)</span>

            <button v-else class="detail-toggle" @click="toggleDetail(m.csvName)">
              Skapas som ny · {{ flagSummary(m.create) }} {{ openDetail === m.csvName ? '▴' : '▾' }}
            </button>
          </div>

          <!-- Detaljer för ny övning -->
          <div v-if="m.action === 'create' && openDetail === m.csvName" class="create-form">
            <label class="cf-field">
              <span>Namn</span>
              <input v-model="m.create.name" type="text" maxlength="80" />
            </label>
            <label class="cf-field">
              <span>Muskelgrupp</span>
              <select v-model="m.create.muscleGroupId" @change="m.create.muscleIds = []">
                <option :value="null">– Ingen –</option>
                <option v-for="g in muscleGroups" :key="g.id" :value="g.id">{{ g.name }}</option>
              </select>
            </label>
            <div v-if="musclesFor(m.create.muscleGroupId).length" class="cf-field wide">
              <span>Muskler</span>
              <div class="chips">
                <button v-for="mu in musclesFor(m.create.muscleGroupId)" :key="mu.id" class="chip-toggle" :class="{ on: m.create.muscleIds.includes(mu.id) }" @click="toggleMuscle(m, mu.id)">{{ mu.name }}</button>
              </div>
            </div>
            <div class="cf-field wide">
              <span>Mäter</span>
              <div class="chips">
                <button v-for="f in FLAGS" :key="f.key" class="chip-toggle" :class="{ on: m.create[f.key] }" @click="m.create[f.key] = !m.create[f.key]">{{ f.label }}</button>
              </div>
            </div>
          </div>
        </div>
        <p v-if="!filteredMappings.length" class="empty">Inga övningar matchar filtret.</p>
        <div v-if="mappingsRemaining > 0" class="more">
          <button class="bulk-btn" @click="mappingLimit += 60">Visa fler ({{ mappingsRemaining }} kvar)</button>
        </div>
      </div>
    </template>

    <!-- ── Pass ── -->
    <template v-else>
      <div class="toolbar">
        <div class="filters">
          <button v-for="f in sessionFilters" :key="f.key" class="fchip" :class="{ on: sessionFilter === f.key }" @click="sessionFilter = f.key">{{ f.label }} <span>{{ fmtInt(f.count) }}</span></button>
        </div>
        <div v-if="years.length > 1" class="filters years">
          <button class="fchip" :class="{ on: year === 'all' }" @click="year = 'all'">Alla år</button>
          <button v-for="y in years" :key="y.year" class="fchip" :class="{ on: year === y.year }" @click="year = y.year">{{ y.year }} <span>{{ fmtInt(y.count) }}</span></button>
        </div>
        <div class="bulk compact">
          <button class="bulk-btn" @click="bulk(filtered, true)">Bocka nya{{ year !== 'all' ? ' ' + year : '' }}</button>
          <button class="bulk-btn" @click="bulk(filtered, false)">Avbocka{{ year !== 'all' ? ' ' + year : ' alla' }}</button>
          <span class="bulk-hint">{{ fmtInt(selectedInFilter) }} av {{ fmtInt(filtered.length) }} valda</span>
        </div>
      </div>
      <div class="rows">
        <template v-for="g in groups" :key="g.key">
          <div class="month">
            <span class="month-label">{{ g.label }}</span>
            <span class="month-meta">{{ fmtInt(g.all.length) }} pass · {{ fmtInt(g.all.filter(i => i.selected).length) }} valda</span>
            <span class="month-actions">
              <button class="mini" title="Bocka alla nya i månaden" @click="bulk(g.all, true)">✓ nya</button>
              <button class="mini" title="Avbocka månaden" @click="bulk(g.all, false)">✕</button>
            </span>
          </div>
          <label v-for="s in g.items" :key="s.id" class="srow" :class="{ off: !s.selected, dup: s.status === 'duplicate' }">
            <input type="checkbox" :checked="s.selected" @change="setSel(s, $event.target.checked)" />
            <div class="srow-main">
              <div class="srow-top">
                <span class="sname">{{ s.name }}</span>
                <span class="badge" :class="statusTone(s.status)">{{ statusLabel(s.status) }}</span>
              </div>
              <div class="srow-meta">
                {{ fmtDate(s.startedAt) }} · {{ s.exercises.length }} övningar · {{ s.setCount }} set
                <span v-if="s.statusReason" class="reason"> · {{ s.statusReason }}</span>
              </div>
              <div class="srow-ex">{{ s.exercises.map(e => e.csvName).join(' · ') }}</div>
            </div>
          </label>
        </template>
        <p v-if="!filtered.length" class="empty">Inga pass matchar filtret.</p>
        <div v-if="remaining > 0" ref="sentinel" class="more">
          <button class="bulk-btn" @click="showMore">Visa fler ({{ fmtInt(remaining) }} kvar)</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { dataset, importState, touch } from '../../../services/import/importSession.js';
import { bestExerciseMatch, MATCH_THRESHOLD, mappingIsReady, STATUS_LABELS, fmtInt } from '../../../services/import/importModel.js';
import { useTimeline } from './useTimeline.js';

const FLAGS = [
  { key: 'tracksWeight', label: 'Vikt' }, { key: 'tracksReps', label: 'Reps' },
  { key: 'tracksDistance', label: 'Distans' }, { key: 'tracksDuration', label: 'Tid' },
  { key: 'isBodyweight', label: 'Kroppsvikt' },
];

export default {
  name: 'ExerciseMappingPanel',
  props: {
    exerciseTypes: { type: Array, default: () => [] },
    muscleGroups: { type: Array, default: () => [] },
    muscles: { type: Array, default: () => [] },
  },
  setup(props) {
    const ds = computed(() => dataset('sessions'));
    const view = ref('exercises');
    const search = ref('');
    const filter = ref('all');
    const openDetail = ref(null);
    const mappingLimit = ref(60);
    const sessionFilter = ref('all');

    const sortedTypes = computed(() => [...props.exerciseTypes].sort((a, b) => a.name.localeCompare(b.name, 'sv')));
    const nameOf = id => props.exerciseTypes.find(e => e.id === id)?.name ?? '?';

    // Mappningarna är få (en per övningsnamn) och reaktiva – v-model fungerar direkt.
    const isTodo = m => !mappingIsReady(m);
    const todoCount = computed(() => ds.value.mappings.filter(isTodo).length);
    const filters = computed(() => {
      const ms = ds.value.mappings;
      return [
        { key: 'all', label: 'Alla', count: ms.length },
        { key: 'todo', label: 'Att lösa', count: ms.filter(isTodo).length },
        { key: 'map', label: 'Kopplade', count: ms.filter(m => m.action === 'map' && m.exerciseTypeId).length },
        { key: 'create', label: 'Nya', count: ms.filter(m => m.action === 'create').length },
        { key: 'ignore', label: 'Ignorerade', count: ms.filter(m => m.action === 'ignore').length },
      ];
    });
    const filteredMappings = computed(() => {
      const q = search.value.trim().toLowerCase();
      return ds.value.mappings.filter(m => {
        if (q && !m.csvName.toLowerCase().includes(q)) return false;
        switch (filter.value) {
          case 'todo': return isTodo(m);
          case 'map': return m.action === 'map' && !!m.exerciseTypeId;
          case 'create': return m.action === 'create';
          case 'ignore': return m.action === 'ignore';
          default: return true;
        }
      });
    });
    const visibleMappings = computed(() => filteredMappings.value.slice(0, mappingLimit.value));
    const mappingsRemaining = computed(() => Math.max(0, filteredMappings.value.length - mappingLimit.value));
    watch([search, filter], () => { mappingLimit.value = 60; });

    function rowClass(m) {
      if (m.action === 'ignore') return 'is-ignore';
      if (m.action === 'create') return 'is-create';
      return m.exerciseTypeId ? 'is-map' : 'is-todo';
    }
    function flagSummary(c) {
      const on = FLAGS.filter(f => c[f.key]).map(f => f.label.toLowerCase());
      return on.length ? on.join('/') : 'inga mätvärden';
    }
    function toggleDetail(name) { openDetail.value = openDetail.value === name ? null : name; }
    function musclesFor(groupId) { return groupId ? props.muscles.filter(mu => mu.muscleGroupId === groupId) : []; }
    function toggleMuscle(m, id) {
      const i = m.create.muscleIds.indexOf(id);
      if (i >= 0) m.create.muscleIds.splice(i, 1); else m.create.muscleIds.push(id);
    }

    function autoMatchUnresolved() {
      for (const m of ds.value.mappings.filter(isTodo)) {
        const { exerciseType, score } = bestExerciseMatch(m.csvName, props.exerciseTypes);
        if (exerciseType && score >= MATCH_THRESHOLD * 0.75) { m.action = 'map'; m.exerciseTypeId = exerciseType.id; }
        else { m.action = 'create'; }
      }
    }
    function setUnresolved(action) { for (const m of ds.value.mappings.filter(isTodo)) m.action = action; }

    // Pass – kan vara många (flera års data): status-/årsfilter, månadsgrupper, fönstrad lista.
    const sessionFilters = computed(() => {
      void importState.rev;
      const it = ds.value.items;
      return [
        { key: 'all', label: 'Alla', count: it.length },
        { key: 'new', label: 'Nya', count: it.filter(s => s.status === 'new').length },
        { key: 'duplicate', label: 'Finns redan', count: it.filter(s => s.status === 'duplicate').length },
      ];
    });
    const statusFiltered = computed(() => {
      void importState.rev;
      return sessionFilter.value === 'all' ? ds.value.items : ds.value.items.filter(s => s.status === sessionFilter.value);
    });
    const { year, years, filtered, groups, remaining, showMore, sentinel } = useTimeline(statusFiltered);
    const selectedInFilter = computed(() => { void importState.rev; return filtered.value.filter(s => s.selected).length; });

    function setSel(s, on) { s.selected = on; touch(); }
    function bulk(list, on) { for (const s of list) s.selected = on ? s.status === 'new' : false; touch(); }

    const fmtDate = iso => new Date(iso).toLocaleString('sv-SE', { dateStyle: 'medium', timeStyle: 'short' });
    const statusLabel = s => STATUS_LABELS[s]?.label ?? s;
    const statusTone = s => STATUS_LABELS[s]?.tone ?? 'grey';

    return {
      ds, view, search, filter, openDetail, sessionFilter, FLAGS, fmtInt,
      sortedTypes, nameOf, todoCount, filters, filteredMappings, visibleMappings, mappingLimit, mappingsRemaining,
      rowClass, flagSummary, toggleDetail, musclesFor, toggleMuscle, autoMatchUnresolved, setUnresolved,
      sessionFilters, year, years, filtered, groups, remaining, showMore, sentinel, selectedInFilter,
      setSel, bulk, fmtDate, statusLabel, statusTone,
    };
  },
};
</script>

<style scoped>
.map-panel { display: flex; flex-direction: column; gap: 0.8rem; }

.seg { display: inline-flex; background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-pill); padding: 3px; align-self: flex-start; }
.seg button { border: none; background: transparent; color: var(--gym-text-muted); padding: 0.4rem 0.95rem; border-radius: var(--gym-radius-pill); font-weight: 700; font-size: 0.86rem; cursor: pointer; display: inline-flex; gap: 0.4rem; align-items: center; transition: background var(--gym-dur) var(--gym-ease), color var(--gym-dur) var(--gym-ease); }
.seg button.on { background: var(--gym-accent); color: var(--gym-on-accent); }
.seg-n { font-size: 0.75rem; opacity: 0.85; }

.toolbar { display: flex; flex-direction: column; gap: 0.55rem; }
.search { padding: 0.55rem 0.8rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); background: var(--gym-surface-2); color: var(--gym-text); font-size: 0.9rem; width: 100%; box-sizing: border-box; }
.search:focus { outline: none; border-color: var(--gym-accent); }
.filters { display: flex; gap: 0.35rem; flex-wrap: wrap; }
.filters.years { padding-top: 0.15rem; border-top: 1px dashed var(--gym-border); }
.fchip { border: 1px solid var(--gym-border); background: var(--gym-surface); color: var(--gym-text-muted); border-radius: var(--gym-radius-pill); padding: 0.28rem 0.7rem; font-size: 0.78rem; font-weight: 700; cursor: pointer; display: inline-flex; gap: 0.35rem; }
.fchip span { opacity: 0.7; }
.fchip.on { background: var(--gym-accent-soft); color: var(--gym-accent); border-color: var(--gym-accent-border); }
.fchip.warn:not(.on) { color: var(--gym-warning); border-color: var(--gym-warning); }

.bulk { display: flex; gap: 0.4rem; flex-wrap: wrap; align-items: center; }
.bulk.compact { margin-top: 0; }
.bulk-btn { border: 1px solid var(--gym-border); background: var(--gym-surface-2); color: var(--gym-text); border-radius: var(--gym-radius-sm); padding: 0.4rem 0.7rem; font-size: 0.8rem; font-weight: 600; cursor: pointer; }
.bulk-btn:hover:not(:disabled) { border-color: var(--gym-accent-border); color: var(--gym-accent); }
.bulk-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.bulk-hint { font-size: 0.74rem; color: var(--gym-text-faint); margin-left: auto; white-space: nowrap; }

.rows { display: flex; flex-direction: column; gap: 0.5rem; }
.empty { color: var(--gym-text-muted); font-size: 0.88rem; text-align: center; padding: 1rem; margin: 0; }
.more { display: flex; justify-content: center; padding: 0.4rem 0; }

.mrow { border: 1px solid var(--gym-border); border-left-width: 4px; border-radius: var(--gym-radius); padding: 0.65rem 0.8rem; background: var(--gym-surface-2); display: flex; flex-direction: column; gap: 0.5rem; }
.mrow.is-map { border-left-color: var(--gym-success); }
.mrow.is-create { border-left-color: var(--gym-warning); }
.mrow.is-todo { border-left-color: var(--gym-danger); background: var(--gym-danger-soft); }
.mrow.is-ignore { border-left-color: var(--gym-border-strong); opacity: 0.6; }

.mrow-top { display: flex; justify-content: space-between; gap: 0.6rem; align-items: baseline; flex-wrap: wrap; }
.mname { font-weight: 700; color: var(--gym-text); font-size: 0.93rem; display: inline-flex; gap: 0.4rem; align-items: center; min-width: 0; }
.mmeta { font-size: 0.78rem; color: var(--gym-text-muted); white-space: nowrap; }
.tag { font-size: 0.7rem; font-weight: 700; padding: 0.1rem 0.45rem; border-radius: var(--gym-radius-pill); }
.tag.link { background: var(--gym-accent-soft); }
.tag.score { background: var(--gym-surface-3); color: var(--gym-text-muted); }
.tag.score.low { background: var(--gym-warning-soft); color: var(--gym-warning); }

.mrow-act { display: flex; gap: 0.6rem; align-items: center; flex-wrap: wrap; }
.actions { display: inline-flex; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); overflow: hidden; flex-shrink: 0; }
.actions button { border: none; background: var(--gym-surface); color: var(--gym-text-muted); padding: 0.38rem 0.7rem; font-size: 0.8rem; font-weight: 700; cursor: pointer; border-right: 1px solid var(--gym-border); }
.actions button:last-child { border-right: none; }
.actions button.on { background: var(--gym-accent); color: var(--gym-on-accent); }
.et-select { flex: 1; min-width: 180px; padding: 0.4rem 0.6rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); background: var(--gym-surface); color: var(--gym-text); font-size: 0.85rem; }
.et-select.missing { border-color: var(--gym-danger); }
.ignored-note { font-size: 0.8rem; color: var(--gym-text-muted); }
.detail-toggle { border: 1px dashed var(--gym-warning); background: var(--gym-warning-soft); color: var(--gym-warning); border-radius: var(--gym-radius-sm); padding: 0.38rem 0.7rem; font-size: 0.8rem; font-weight: 600; cursor: pointer; text-align: left; }

.create-form { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem; padding: 0.6rem; background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); }
.cf-field { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.78rem; color: var(--gym-text-muted); font-weight: 600; }
.cf-field.wide { grid-column: 1 / -1; }
.cf-field input, .cf-field select { padding: 0.45rem 0.6rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); background: var(--gym-surface-2); color: var(--gym-text); font-size: 0.86rem; width: 100%; box-sizing: border-box; }
.chips { display: flex; gap: 0.35rem; flex-wrap: wrap; }
.chip-toggle { border: 1px solid var(--gym-border); background: var(--gym-surface-2); color: var(--gym-text-muted); border-radius: var(--gym-radius-pill); padding: 0.25rem 0.65rem; font-size: 0.78rem; font-weight: 600; cursor: pointer; }
.chip-toggle.on { background: var(--gym-accent-soft); color: var(--gym-accent); border-color: var(--gym-accent-border); }

/* Månadsrubrik */
.month { display: flex; align-items: center; gap: 0.6rem; position: sticky; top: -0.5rem; z-index: 2; background: var(--gym-surface); padding: 0.45rem 0.2rem 0.3rem; margin-top: 0.3rem; border-bottom: 1px solid var(--gym-border); }
.month:first-child { margin-top: 0; }
.month-label { font-weight: 800; color: var(--gym-text); font-size: 0.86rem; text-transform: capitalize; }
.month-meta { font-size: 0.76rem; color: var(--gym-text-muted); }
.month-actions { margin-left: auto; display: inline-flex; gap: 0.3rem; }
.mini { border: 1px solid var(--gym-border); background: var(--gym-surface-2); color: var(--gym-text-muted); border-radius: var(--gym-radius-pill); padding: 0.15rem 0.55rem; font-size: 0.72rem; font-weight: 700; cursor: pointer; }
.mini:hover { color: var(--gym-accent); border-color: var(--gym-accent-border); }

/* Pass-lista */
.srow { display: flex; gap: 0.7rem; align-items: flex-start; border: 1px solid var(--gym-border); border-radius: var(--gym-radius); padding: 0.6rem 0.8rem; background: var(--gym-surface-2); cursor: pointer; }
.srow.off { opacity: 0.6; }
.srow input { margin-top: 0.25rem; accent-color: var(--gym-accent); width: 18px; height: 18px; flex-shrink: 0; }
.srow-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.2rem; }
.srow-top { display: flex; justify-content: space-between; gap: 0.5rem; align-items: center; }
.sname { font-weight: 700; color: var(--gym-text); font-size: 0.92rem; }
.srow-meta { font-size: 0.78rem; color: var(--gym-text-muted); }
.reason { color: var(--gym-text-faint); }
.srow-ex { font-size: 0.76rem; color: var(--gym-text-faint); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.badge { font-size: 0.7rem; font-weight: 700; padding: 0.15rem 0.5rem; border-radius: var(--gym-radius-pill); white-space: nowrap; }
.badge.ok { background: var(--gym-success-soft); color: var(--gym-success); }
.badge.grey { background: var(--gym-surface-3); color: var(--gym-text-muted); }
.badge.warn { background: var(--gym-warning-soft); color: var(--gym-warning); }
.badge.info { background: var(--gym-accent-soft); color: var(--gym-accent); }
.badge.danger { background: var(--gym-danger-soft); color: var(--gym-danger); }

@media (max-width: 600px) {
  .bulk { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 0.2rem; scrollbar-width: none; }
  .bulk-btn { white-space: nowrap; flex-shrink: 0; }
  .filters.years { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; }
  .filters.years .fchip { flex-shrink: 0; }
  .create-form { grid-template-columns: 1fr; }
  .bulk-hint { display: none; }
  .month-meta { display: none; }
  .et-select { min-width: 0; width: 100%; }
}
</style>
